import { numFont } from '../../../utils/fonts';
import React from 'react';
import { Avatar, Box, LinearProgress, Typography } from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import { useTranslation } from 'react-i18next';

import { hms } from '../../worktime/clock/worktimeLib';
import { useWidgetFrame } from '../frameContext';
import { avatarColor } from './format';

const STATE_COLORS = { clocked: '#4299e1', break: '#d97706', day: '#16a34a', none: '#cbd5e1', absent: '#cbd5e1', warn: '#dc2626' };

/**
 * People board (design template 6): one compact card per employee.
 * people: [{ id, initials, name, state, plate, workNumber, elapsedSec, doneSec, daySec, onClick, colorIdx }]
 */
export default function PeopleBody({ people = [] }) {
  const { t } = useTranslation();
  const frame = useWidgetFrame();
  const cols = frame?.isMobile ? 2 : frame?.size === 'L' ? 6 : 3;
  const rows = frame?.size === 'L' ? 2 : 2;
  const shown = frame?.isMobile ? people : people.slice(0, cols * rows);

  return (
    <Box sx={{ flex: 1, minHeight: 0, p: 1.5, display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 1.25, alignContent: 'start', overflow: 'hidden' }}>
      {shown.map((person, index) => {
        const color = STATE_COLORS[person.state] || STATE_COLORS.none;
        const pct = person.daySec ? Math.min(100, Math.round((person.doneSec / person.daySec) * 100)) : 0;
        return (
          <Box
            key={person.id}
            onClick={person.onClick && frame?.mode !== 'edit' ? person.onClick : undefined}
            sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', px: 1.5, py: 1.25, opacity: person.state === 'absent' ? 0.6 : 1, cursor: person.onClick ? 'pointer' : 'default', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
              <Avatar sx={{ width: 24, height: 24, fontSize: 10, fontWeight: 700, bgcolor: avatarColor(person.colorIdx ?? index) }}>{person.initials}</Avatar>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                {person.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: 'text.secondary', minWidth: 0 }}>
              {person.state === 'clocked' && person.workNumber ? (
                <>
                  <TimerIcon sx={{ fontSize: 14, color }} />
                  <Box component="span" sx={{ ...numFont, color: '#1a365d', fontWeight: 700 }}>
                    #{person.workNumber}
                  </Box>
                  <Box component="span" sx={{ ...numFont }}>{hms(person.elapsedSec || 0)}</Box>
                </>
              ) : (
                <Typography noWrap sx={{ fontSize: 12, color: person.state === 'warn' ? '#dc2626' : 'text.secondary' }}>
                  {t(`dashboard.people.${person.state === 'clocked' ? 'clocked' : person.state}`)}
                </Typography>
              )}
            </Box>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 4, borderRadius: 2, bgcolor: '#edf2f7', '& .MuiLinearProgress-bar': { bgcolor: pct >= 100 ? '#16a34a' : '#3182ce' } }} />
            <Typography sx={{ ...numFont, fontSize: 11, color: 'text.secondary' }}>
              {pct} % · {(person.doneSec / 3600).toFixed(1).replace('.', ',')} / {(person.daySec / 3600).toFixed(1).replace('.', ',')} h
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
